import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Divider,
  Chip,
  Alert,
  IconButton,
  CircularProgress,
  Grid,
  Paper,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as CancelIcon,
  Person as PersonIcon,
  Groups as GroupIcon,
  CalendarToday as CalendarIcon,
  AccountCircle as OfficerIcon,
  AccountBalance as MoneyIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

const RequestReviewDialog = ({ request, reviewContext, loadingContext, open, onClose, onApprove, onReject, onCancel }) => {
  const [actionNotes, setActionNotes] = useState('');
  const [showApprovalInput, setShowApprovalInput] = useState(false);
  const [showRejectionInput, setShowRejectionInput] = useState(false);
  const [showCancelInput, setShowCancelInput] = useState(false);

  if (!request) return null;

  const formatCurrency = (amount) => `KSh ${parseFloat(amount || 0).toLocaleString()}`;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      partial_withdrawal: 'Partial Withdrawal',
      full_withdrawal: 'Full Withdrawal',
      account_offset: 'Account Offset',
      account_deactivation: 'Account Deactivation',
      savings_transfer: 'Savings Transfer',
      group_transfer: 'Group Transfer',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      partial_withdrawal: '#2196f3',
      full_withdrawal: '#f44336',
      account_offset: '#ff9800',
      account_deactivation: '#9c27b0',
      savings_transfer: '#4caf50',
      group_transfer: '#00bcd4',
    };
    return colors[type] || '#757575';
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'executed': return 'info';
      default: return 'default';
    }
  };

  const handleApproveClick = () => {
    if (request.status === 'pending') {
      setShowApprovalInput(true);
      setShowRejectionInput(false);
      setShowCancelInput(false);
    }
  };

  const handleRejectClick = () => {
    if (request.status === 'pending') {
      setShowRejectionInput(true);
      setShowApprovalInput(false);
      setShowCancelInput(false);
    }
  };

  const handleCancelClick = () => {
    if (request.status === 'pending' || request.status === 'approved') {
      setShowCancelInput(true);
      setShowApprovalInput(false);
      setShowRejectionInput(false);
    }
  };

  const handleConfirmApprove = () => {
    onApprove(request.id, actionNotes);
    setActionNotes('');
    setShowApprovalInput(false);
  };

  const handleConfirmReject = () => {
    if (actionNotes.trim()) {
      onReject(request.id, actionNotes);
      setActionNotes('');
      setShowRejectionInput(false);
    }
  };

  const handleConfirmCancel = () => {
    if (actionNotes.trim()) {
      onCancel(request.id, actionNotes);
      setActionNotes('');
      setShowCancelInput(false);
    }
  };

  const handleClose = () => {
    setActionNotes('');
    setShowApprovalInput(false);
    setShowRejectionInput(false);
    setShowCancelInput(false);
    onClose();
  };

  const KV = ({ label, value }) => (
    <Typography sx={{ mb: 1 }}>
      <strong>{label}:</strong> {value ?? 'N/A'}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.default',
          fontWeight: 'bold',
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          Request Review
          <Chip
            label={getTypeLabel(request.type)}
            size="small"
            sx={{ backgroundColor: getTypeColor(request.type), color: '#ffffff', fontWeight: 600 }}
          />
          <Chip
            label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            color={getStatusChipColor(request.status)}
            size="small"
          />
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loadingContext && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loadingContext && reviewContext && (
          <Grid container spacing={3}>
            {/* Member Information Card */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  Member Information
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: '#1976d2' }}>
                    {reviewContext.member_name?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {reviewContext.member_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {reviewContext.member_number}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <GroupIcon fontSize="small" />
                  {reviewContext.member_group || 'No Group'}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  {reviewContext.member_phone || 'N/A'}
                </Typography>
              </Paper>
            </Grid>

            {/* Current Balances Card */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  Current Balances
                </Typography>
                <Box sx={{ '& > *': { mb: 1 } }}>
                  <KV label="Savings Balance" value={formatCurrency(reviewContext.savings_balance)} />
                  <KV label="Loan Balance" value={formatCurrency(reviewContext.loan_balance)} />
                  <KV label="Advance Balance" value={formatCurrency(reviewContext.advance_balance)} />
                  {reviewContext.school_fees_advance_balance ? (
                    <KV label="School Fees Advance" value={formatCurrency(reviewContext.school_fees_advance_balance)} />
                  ) : null}
                </Box>
              </Paper>
            </Grid>

            {/* Request Details */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  Request Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Request Type</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {reviewContext.request_type}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  {reviewContext.requested_amount && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon color="action" />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Requested Amount</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(reviewContext.requested_amount)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {reviewContext.processing_charge && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">Processing Charge</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(reviewContext.processing_charge)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {reviewContext.release_amount && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">Release Amount</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(reviewContext.release_amount)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <OfficerIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Filled By</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {reviewContext.filled_by_name || reviewContext.filled_by}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Date Filled</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatDate(reviewContext.date_filled)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Reason */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  Reason
                </Typography>
                <Typography variant="body2" sx={{ p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                  {reviewContext.reason || request.reason}
                </Typography>
              </Paper>
            </Grid>

            {/* Additional Info */}
            {reviewContext.additional_info && Object.keys(reviewContext.additional_info).length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    Additional Information
                  </Typography>
                  <Box sx={{ backgroundColor: 'action.hover', borderRadius: 1, p: 2 }}>
                    {Object.entries(reviewContext.additional_info).map(([key, value]) => (
                      <KV key={key} label={key.replace(/_/g, ' ')} value={String(value)} />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Status-specific information */}
        {request.status === 'approved' && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This request was approved on {formatDate(request.approvalDate)}
            </Typography>
            {request.approvalNotes && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Notes: {request.approvalNotes}
              </Typography>
            )}
          </Alert>
        )}

        {request.status === 'rejected' && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              This request was rejected on {formatDate(request.rejectionDate)}
            </Typography>
            {request.rejectionReason && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Reason: {request.rejectionReason}
              </Typography>
            )}
          </Alert>
        )}

        {/* Action Input Fields */}
        {request.status === 'pending' && showApprovalInput && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Approval Notes (Optional)"
              placeholder="Add any notes or instructions..."
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
            />
          </Box>
        )}

        {request.status === 'pending' && showRejectionInput && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason (Required)"
              placeholder="Provide a clear reason for rejection..."
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              required
              error={showRejectionInput && !actionNotes.trim()}
              helperText={showRejectionInput && !actionNotes.trim() ? 'Rejection reason is required' : ''}
            />
          </Box>
        )}

        {(request.status === 'pending' || request.status === 'approved') && showCancelInput && (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Cancellation Reason (Required)"
              placeholder="Provide a reason for cancellation..."
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              required
              error={showCancelInput && !actionNotes.trim()}
              helperText={showCancelInput && !actionNotes.trim() ? 'Cancellation reason is required' : ''}
            />
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>

        {request.status === 'pending' && !showApprovalInput && !showRejectionInput && !showCancelInput && (
          <>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<CancelIcon />}
              onClick={handleCancelClick}
            >
              Cancel Request
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              onClick={handleRejectClick}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={handleApproveClick}
            >
              Approve
            </Button>
          </>
        )}

        {showApprovalInput && (
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            onClick={handleConfirmApprove}
          >
            Confirm Approval
          </Button>
        )}

        {showRejectionInput && (
          <Button
            variant="contained"
            color="error"
            startIcon={<RejectIcon />}
            onClick={handleConfirmReject}
            disabled={!actionNotes.trim()}
          >
            Confirm Rejection
          </Button>
        )}

        {showCancelInput && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={handleConfirmCancel}
            disabled={!actionNotes.trim()}
          >
            Confirm Cancellation
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequestReviewDialog;